/**
 * Rating Widget
 * 
 * A reusable 5-point rating component for kinks, actions, gear, etc.
 * Integrates with PersonaGate to require persona before rating.
 * 
 * Rating Scale:
 * -2: Hard Limit (never, absolute boundary)
 * -1: Soft Limit (unlikely, needs discussion)
 *  0: Neutral (no strong feeling)
 * +1: Curious (interested, want to explore)
 * +2: Into It (enthusiastic, actively want)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, HeartOff, Minus, Sparkles, Star, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePersonaGate } from '../contexts/PersonaGateContext';
import RatingModifierPanel from './RatingModifierPanel';
import { API_BASE } from '../utils/api';

const RATING_CONFIG = {
    '-2': {
        label: 'Hard Limit',
        shortLabel: 'Limit',
        color: 'text-red-300',
        bg: 'bg-red-950/60',
        activeBg: 'bg-gradient-to-b from-red-900/80 to-red-950/90',
        borderColor: 'border-red-500/60',
        hoverBg: 'hover:bg-red-900/50',
        glow: 'shadow-[0_0_12px_rgba(239,68,68,0.3)]',
        icon: HeartOff
    },
    '-1': {
        label: 'Soft Limit',
        shortLabel: 'Soft',
        color: 'text-amber-300',
        bg: 'bg-amber-950/60',
        activeBg: 'bg-gradient-to-b from-amber-900/80 to-amber-950/90',
        borderColor: 'border-amber-500/60',
        hoverBg: 'hover:bg-amber-900/50',
        glow: 'shadow-[0_0_12px_rgba(245,158,11,0.3)]',
        icon: Minus
    },
    '0': {
        label: 'Neutral',
        shortLabel: 'Meh',
        color: 'text-slate-300',
        bg: 'bg-slate-800/60',
        activeBg: 'bg-gradient-to-b from-slate-700/80 to-slate-800/90',
        borderColor: 'border-slate-500/60',
        hoverBg: 'hover:bg-slate-700/50',
        glow: 'shadow-[0_0_8px_rgba(148,163,184,0.2)]',
        icon: Minus
    },
    '1': {
        label: 'Curious',
        shortLabel: 'Curious',
        color: 'text-cyan-300',
        bg: 'bg-cyan-950/60',
        activeBg: 'bg-gradient-to-b from-cyan-900/80 to-cyan-950/90',
        borderColor: 'border-cyan-500/60',
        hoverBg: 'hover:bg-cyan-900/50',
        glow: 'shadow-[0_0_12px_rgba(34,211,238,0.3)]',
        icon: Sparkles
    },
    '2': {
        label: 'Into It',
        shortLabel: 'Into It',
        color: 'text-emerald-300',
        bg: 'bg-emerald-950/60',
        activeBg: 'bg-gradient-to-b from-emerald-900/80 to-emerald-950/90',
        borderColor: 'border-emerald-500/60',
        hoverBg: 'hover:bg-emerald-900/50',
        glow: 'shadow-[0_0_12px_rgba(52,211,153,0.3)]',
        icon: Heart
    }
};

function RatingWidget({
    entityType,      // 'kink', 'sex_action', 'gear', 'arousal_mechanic'
    entityId,        // UUID of the entity
    perspective = null, // 'dom', 'sub', 'fantasy_only' (NEW for perspective ratings)
    initialRating = null,  // Current rating if known
    size = 'md',     // 'sm', 'md', 'lg'
    variant = 'full', // 'full' (all 5), 'compact' (icons only), 'buttons' (inline buttons)
    onRatingChange,  // Callback after rating saved
    showLabels = true,
    showModifiers = false, // Show experience/context/role modifiers
    disabled = false,
    autoFetch = true // If true, fetch existing rating on mount
}) {
    const { activePersona, getAuthHeaders } = useAuth();
    const { requirePersona } = usePersonaGate();

    const [currentRating, setCurrentRating] = useState(initialRating);
    const [modifiers, setModifiers] = useState({});
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hoveredRating, setHoveredRating] = useState(null);

    // Fetch existing rating when component mounts (if autoFetch enabled)
    useEffect(() => {
        const fetchExistingRating = async () => {
            if (!autoFetch || !activePersona || !entityType || !entityId) return;
            if (initialRating !== null) return; // Already have a rating

            setLoading(true);
            try {
                const headers = await getAuthHeaders();
                // Include perspective in URL if provided
                const perspectiveParam = perspective ? `?perspective=${perspective}` : '';
                const response = await fetch(
                    `${API_BASE}/api/ratings/${activePersona.id}/${entityType}/${entityId}${perspectiveParam}`,
                    { headers }
                );

                if (response.ok) {
                    const data = await response.json();
                    if (data.rating !== undefined && data.rating !== null) {
                        setCurrentRating(data.rating);
                    }
                    // Also load modifiers
                    setModifiers({
                        experience: data.experience || '',
                        context: data.context || '',
                        role: data.role || '',
                        notes: data.notes || ''
                    });
                }
            } catch (err) {
                // Silent fail - user just hasn't rated this yet
                console.debug('No existing rating found');
            } finally {
                setLoading(false);
            }
        };

        fetchExistingRating();
    }, [activePersona, entityType, entityId, perspective, autoFetch, initialRating, getAuthHeaders]);

    // Sync with initialRating prop
    useEffect(() => {
        if (initialRating !== null) {
            setCurrentRating(initialRating);
        }
    }, [initialRating]);

    const handleRating = async (rating) => {
        if (disabled || saving) return;

        // Require persona before rating
        requirePersona(async (persona) => {
            setSaving(true);

            try {
                const headers = await getAuthHeaders();
                const response = await fetch(`${API_BASE}/api/ratings`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...headers
                    },
                    body: JSON.stringify({
                        persona_id: persona.id,
                        entity_type: entityType,
                        entity_id: entityId,
                        perspective: perspective, // Include perspective if set
                        rating: rating
                    })
                });

                if (!response.ok) {
                    throw new Error('Failed to save rating');
                }

                setCurrentRating(rating);

                if (onRatingChange) {
                    onRatingChange(rating);
                }
            } catch (err) {
                console.error('Rating error:', err);
            } finally {
                setSaving(false);
            }
        });
    };

    const sizeClasses = {
        sm: 'text-xs gap-0.5',
        md: 'text-sm gap-1',
        lg: 'text-base gap-1.5'
    };

    const buttonSizes = {
        sm: 'px-2.5 py-1.5',
        md: 'px-4 py-2',
        lg: 'px-5 py-2.5'
    };

    const iconSizes = {
        sm: 14,
        md: 18,
        lg: 22
    };

    // Compact variant: just 5 icon buttons
    if (variant === 'compact') {
        return (
            <div className={`flex items-center ${sizeClasses[size]}`}>
                {[-2, -1, 0, 1, 2].map((rating) => {
                    const config = RATING_CONFIG[rating.toString()];
                    const Icon = config.icon;
                    const isActive = currentRating === rating;
                    const isHovered = hoveredRating === rating;

                    return (
                        <button
                            key={rating}
                            onClick={() => handleRating(rating)}
                            onMouseEnter={() => setHoveredRating(rating)}
                            onMouseLeave={() => setHoveredRating(null)}
                            disabled={disabled || saving}
                            className={`
                                ${buttonSizes[size]} transition-all duration-200 backdrop-blur-sm
                                ${isActive
                                    ? `${config.activeBg} ${config.color} border-2 ${config.borderColor} ${config.glow}`
                                    : isHovered
                                        ? `${config.bg} ${config.color} border border-white/20`
                                        : `bg-dark-900/70 text-dark-400 border border-dark-700/50 ${config.hoverBg}`
                                }
                                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-105'}
                            `}
                            title={config.label}
                        >
                            {saving && isActive ? (
                                <Loader2 className="animate-spin" size={iconSizes[size]} />
                            ) : (
                                <Icon size={iconSizes[size]} className={isActive ? 'drop-shadow-lg' : ''} />
                            )}
                        </button>
                    );
                })}
            </div>
        );
    }

    // Buttons variant: horizontal button strip
    if (variant === 'buttons') {
        return (
            <div className={`flex items-center ${sizeClasses[size]}`}>
                {[-2, -1, 0, 1, 2].map((rating) => {
                    const config = RATING_CONFIG[rating.toString()];
                    const isActive = currentRating === rating;

                    return (
                        <button
                            key={rating}
                            onClick={() => handleRating(rating)}
                            disabled={disabled || saving}
                            className={`
                                ${buttonSizes[size]} font-medium transition-all duration-200 backdrop-blur-sm
                                ${isActive
                                    ? `${config.activeBg} ${config.color} border-2 ${config.borderColor} ${config.glow}`
                                    : `bg-dark-900/70 text-dark-400 border border-dark-700/50 hover:border-dark-500 hover:text-dark-200`
                                }
                                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-105'}
                            `}
                        >
                            {saving && isActive ? (
                                <Loader2 className="animate-spin inline-block" size={iconSizes[size]} />
                            ) : (
                                showLabels ? config.shortLabel : rating
                            )}
                        </button>
                    );
                })}
            </div>
        );
    }

    // Full variant: detailed selector with labels
    return (
        <div className="space-y-2">
            {showLabels && currentRating !== null && (
                <div className={`text-sm ${RATING_CONFIG[currentRating.toString()].color} font-medium`}>
                    {RATING_CONFIG[currentRating.toString()].label}
                </div>
            )}

            <div className={`flex items-center ${sizeClasses[size]}`}>
                {[-2, -1, 0, 1, 2].map((rating) => {
                    const config = RATING_CONFIG[rating.toString()];
                    const Icon = config.icon;
                    const isActive = currentRating === rating;
                    const isHovered = hoveredRating === rating;

                    return (
                        <motion.button
                            key={rating}
                            onClick={() => handleRating(rating)}
                            onMouseEnter={() => setHoveredRating(rating)}
                            onMouseLeave={() => setHoveredRating(null)}
                            disabled={disabled || saving}
                            whileHover={{ scale: 1.08 }}
                            whileTap={{ scale: 0.95 }}
                            className={`
                                flex flex-col items-center ${buttonSizes[size]} transition-all duration-200 backdrop-blur-sm
                                ${isActive
                                    ? `${config.activeBg} ${config.color} border-2 ${config.borderColor} ${config.glow}`
                                    : isHovered
                                        ? `${config.bg} ${config.color} border border-white/20`
                                        : `bg-dark-900/70 text-dark-400 border border-dark-700/50 ${config.hoverBg}`
                                }
                                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            `}
                        >
                            {saving && isActive ? (
                                <Loader2 className="animate-spin" size={iconSizes[size]} />
                            ) : (
                                <Icon size={iconSizes[size]} className={isActive ? 'drop-shadow-lg' : ''} />
                            )}
                            {showLabels && size !== 'sm' && (
                                <span className={`mt-1.5 text-xs font-medium tracking-wide ${isActive ? 'opacity-100' : 'opacity-70'}`}>{config.shortLabel}</span>
                            )}
                        </motion.button>
                    );
                })}
            </div>

            {/* Modifier Panel - shown when showModifiers is true and has a rating */}
            {showModifiers && currentRating !== null && activePersona && (
                <RatingModifierPanel
                    entityType={entityType}
                    entityId={entityId}
                    personaId={activePersona.id}
                    initialModifiers={modifiers}
                    onModifiersChange={(newMods) => setModifiers(newMods)}
                />
            )}
        </div>
    );
}

export default RatingWidget;
